import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Pencil, Trash2, Eye } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useBuildings, useDeleteBuilding } from '@/hooks'
import { PageHeader, EmptyState, DataTable, SortableHeader, ConfirmDialog } from '@/components'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Building } from '@/types'
import { BuildingFormDialog } from './BuildingFormDialog'

export function BuildingsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const { data: buildings, isLoading, error } = useBuildings(effectiveOrgId)
  const deleteMutation = useDeleteBuilding(effectiveOrgId)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editBuilding, setEditBuilding] = useState<Building | null>(null)
  const [deleteBuilding, setDeleteBuilding] = useState<Building | null>(null)

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  // Super admin with no org selected
  if (user?.role === 'super_admin' && !effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Buildings"
          description="Manage buildings in your organization"
        />
        <EmptyState
          title="No Organization Selected"
          description="Select an organization using the switcher in the header to view buildings."
        />
      </div>
    )
  }

  const columns: ColumnDef<Building>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <Link
          to={`/buildings/${row.original.id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.getValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'address',
      header: ({ column }) => (
        <SortableHeader column={column}>Address</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.getValue('address') || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/buildings/${row.original.id}`}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </Link>
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onClick={() => setEditBuilding(row.original)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteBuilding(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load buildings: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Buildings"
        description="Manage buildings in your organization"
        actions={
          canEdit && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Building
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading buildings...</div>
      ) : buildings && buildings.length > 0 ? (
        <DataTable
          columns={columns}
          data={buildings}
          filterColumn="name"
          filterPlaceholder="Filter by name..."
        />
      ) : (
        <EmptyState
          title="No buildings"
          description="Get started by adding your first building."
          action={
            canEdit && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Building
              </Button>
            )
          }
        />
      )}

      <BuildingFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editBuilding && (
        <BuildingFormDialog
          open={!!editBuilding}
          onOpenChange={(open) => !open && setEditBuilding(null)}
          building={editBuilding}
        />
      )}

      <ConfirmDialog
        open={!!deleteBuilding}
        onOpenChange={(open) => !open && setDeleteBuilding(null)}
        title="Delete Building"
        description={`Are you sure you want to delete "${deleteBuilding?.name}"? This will also delete all rooms associated with this building. This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteBuilding) {
            deleteMutation.mutate(deleteBuilding.id, {
              onSuccess: () => setDeleteBuilding(null),
            })
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
